import * as Cesium from "cesium";
import { disableCameraControls, restoreCameraControls } from "../MapUtility";

export function selectEntitiesInRectangleOnce(viewer, mapObject, originalCameraControls, options = {}) {
  const { onActiveChange } = options;

  let resolvePromise;
  let rejectPromise;

  const promise = new Promise((resolve, reject) => {
    resolvePromise = resolve;
    rejectPromise = reject;
  });

  const scene = viewer.scene;
  const canvas = viewer.canvas;

  // --- Overlay box ---
  const container = viewer.container;
  const box = document.createElement("div");
  box.style.position = "absolute";
  box.style.border = "2px solid #4da3ff";
  box.style.background = "rgba(77,163,255,0.15)";
  box.style.pointerEvents = "none";
  box.style.display = "none";
  container.appendChild(box);

  const handler = new Cesium.ScreenSpaceEventHandler(canvas);

  let drawing = false;
  let startPx = null;
  let endPx = null;
  let finished = false;
  let cameraLocked = false;

  const roundTo = (v, decimals) => {
    const n = Number(v);
    if (!Number.isFinite(n)) return n;
    const f = 10 ** decimals;
    return Math.round(n * f) / f;
  };

  const cartesianToLonLatHeight = (cartesian) => {
    const c = Cesium.Cartographic.fromCartesian(cartesian);
    const lon = roundTo(Cesium.Math.toDegrees(c.longitude), 7);
    const lat = roundTo(Cesium.Math.toDegrees(c.latitude), 7);
    const h = Math.max(0, roundTo(c.height ?? 0, 1)); // height: 1 decimal, non-negative
    return [lon, lat, h];
  };

  const getLayerIdFromDataSource = (ds) => ds?.layerId ?? ds?._layerId ?? ds?.name ?? ds?.id;
  const getLayerNameFromDataSource = (ds) => ds?.layerName ?? String(getLayerIdFromDataSource(ds) ?? "");

  const extractEntityProperties = (entity, time) => {
    const bag = entity?.properties;
    if (!bag) return {};

    const names = Array.isArray(bag.propertyNames) ? bag.propertyNames : [];
    const out = {};

    for (const name of names) {
      const prop = bag[name];
      try {
        // Cesium Property -> value at time
        if (prop && typeof prop.getValue === "function") {
          const v = prop.getValue(time);
          if (v !== undefined) out[name] = v;
        } else if (prop !== undefined) {
          // Fallback (rare)
          out[name] = prop;
        }
      } catch {}
    }

    return out;
  };

  const entityToGeoJsonFeature = (entity, time) => {
    const properties = extractEntityProperties(entity, time);

    // Point-like (billboard/label/model/point usually has position)
    if (entity?.position) {
      const pos = entity.position.getValue(time);
      if (pos) {
        return {
          type: "Feature",
          geometry: { type: "Point", coordinates: cartesianToLonLatHeight(pos) },
          properties,
        };
      }
    }

    // Rectangle entity -> Polygon
    if (entity?.rectangle?.coordinates) {
      const rect = entity.rectangle.coordinates.getValue(time);
      if (rect) {
        const corners = [Cesium.Rectangle.southwest(rect), Cesium.Rectangle.southeast(rect), Cesium.Rectangle.northeast(rect), Cesium.Rectangle.northwest(rect), Cesium.Rectangle.southwest(rect)].map(
          (c) => {
            const lon = roundTo(Cesium.Math.toDegrees(c.longitude), 7);
            const lat = roundTo(Cesium.Math.toDegrees(c.latitude), 7);
            const h = 0; // rectangle corners don't define height; keep 0
            return [lon, lat, h];
          }
        );

        return {
          type: "Feature",
          geometry: { type: "Polygon", coordinates: [corners] },
          properties,
        };
      }
    }

    // Polygon entity
    if (entity?.polygon?.hierarchy) {
      const h = entity.polygon.hierarchy.getValue(time);
      const positions = h?.positions || [];
      if (positions.length) {
        const ring = positions.map(cartesianToLonLatHeight);
        // ensure closed ring
        const first = ring[0];
        const last = ring[ring.length - 1];
        if (!last || last[0] !== first[0] || last[1] !== first[1] || last[2] !== first[2]) {
          ring.push([...first]);
        }

        return {
          type: "Feature",
          geometry: { type: "Polygon", coordinates: [ring] },
          properties,
        };
      }
    }

    // Polyline entity
    if (entity?.polyline?.positions) {
      const positions = entity.polyline.positions.getValue(time) || [];
      if (positions.length) {
        return {
          type: "Feature",
          geometry: { type: "LineString", coordinates: positions.map(cartesianToLonLatHeight) },
          properties,
        };
      }
    }

    // Corridor/Wall -> treat as LineString from positions
    if (entity?.corridor?.positions) {
      const positions = entity.corridor.positions.getValue(time) || [];
      if (positions.length) {
        return {
          type: "Feature",
          geometry: { type: "LineString", coordinates: positions.map(cartesianToLonLatHeight) },
          properties,
        };
      }
    }

    if (entity?.wall?.positions) {
      const positions = entity.wall.positions.getValue(time) || [];
      if (positions.length) {
        return {
          type: "Feature",
          geometry: { type: "LineString", coordinates: positions.map(cartesianToLonLatHeight) },
          properties,
        };
      }
    }

    return null;
  };

  const cleanup = () => {
    if (finished) return;
    finished = true;

    try {
      if (cameraLocked) restoreCameraControls(viewer, originalCameraControls);
    } catch {}

    try {
      handler.destroy();
    } catch {}
    try {
      box.remove();
    } catch {}

    window.removeEventListener("keydown", onKeyDown);

    if (onActiveChange) onActiveChange(false);
  };

  const cancel = () => {
    cleanup();
    resolvePromise([]); // cancel => empty array
  };

  const onKeyDown = (e) => {
    if (e.key === "Escape") cancel();
  };
  window.addEventListener("keydown", onKeyDown);

  const setBox = (a, b) => {
    const left = Math.min(a.x, b.x);
    const top = Math.min(a.y, b.y);
    const width = Math.abs(a.x - b.x);
    const height = Math.abs(a.y - b.y);
    box.style.left = `${left}px`;
    box.style.top = `${top}px`;
    box.style.width = `${width}px`;
    box.style.height = `${height}px`;
    box.style.display = "block";
  };

  const pickCartesianOnSurface = (screenPos) => {
    if (scene.pickPositionSupported) {
      const p = scene.pickPosition(screenPos);
      if (Cesium.defined(p)) return p;
    }
    const ray = viewer.camera.getPickRay(screenPos);
    if (!ray) return null;
    const p = scene.globe.pick(ray, scene);
    return Cesium.defined(p) ? p : null;
  };

  const pickCartographic = (screenPos) => {
    const cart = pickCartesianOnSurface(screenPos);
    if (!cart) return null;
    return Cesium.Cartographic.fromCartesian(cart);
  };

  const rectangleFromTwoPixels = (p1, p2) => {
    const c1 = pickCartographic(p1);
    const c2 = pickCartographic(p2);
    if (!c1 || !c2) return null;

    const west = Math.min(c1.longitude, c2.longitude);
    const east = Math.max(c1.longitude, c2.longitude);
    const south = Math.min(c1.latitude, c2.latitude);
    const north = Math.max(c1.latitude, c2.latitude);

    return new Cesium.Rectangle(west, south, east, north);
  };

  const rectIntersects = (a, b) => {
    return !(b.west > a.east || b.east < a.west || b.south > a.north || b.north < a.south);
  };

  const getEntityRectApprox = (entity, time) => {
    // Point-like
    if (entity?.position) {
      const pos = entity.position.getValue(time);
      if (pos) {
        const c = Cesium.Cartographic.fromCartesian(pos);
        return new Cesium.Rectangle(c.longitude, c.latitude, c.longitude, c.latitude);
      }
    }

    // Rectangle
    if (entity?.rectangle?.coordinates) {
      const r = entity.rectangle.coordinates.getValue(time);
      if (r) return r;
    }

    // Polygon
    if (entity?.polygon?.hierarchy) {
      const h = entity.polygon.hierarchy.getValue(time);
      const positions = h?.positions || [];
      if (positions.length) {
        const cartos = positions.map((p) => Cesium.Cartographic.fromCartesian(p));
        const lons = cartos.map((c) => c.longitude);
        const lats = cartos.map((c) => c.latitude);
        return new Cesium.Rectangle(Math.min(...lons), Math.min(...lats), Math.max(...lons), Math.max(...lats));
      }
    }

    // Polyline
    if (entity?.polyline?.positions) {
      const positions = entity.polyline.positions.getValue(time) || [];
      if (positions.length) {
        const cartos = positions.map((p) => Cesium.Cartographic.fromCartesian(p));
        const lons = cartos.map((c) => c.longitude);
        const lats = cartos.map((c) => c.latitude);
        return new Cesium.Rectangle(Math.min(...lons), Math.min(...lats), Math.max(...lons), Math.max(...lats));
      }
    }

    // Corridor/Wall
    if (entity?.corridor?.positions) {
      const positions = entity.corridor.positions.getValue(time) || [];
      if (positions.length) {
        const cartos = positions.map((p) => Cesium.Cartographic.fromCartesian(p));
        const lons = cartos.map((c) => c.longitude);
        const lats = cartos.map((c) => c.latitude);
        return new Cesium.Rectangle(Math.min(...lons), Math.min(...lats), Math.max(...lons), Math.max(...lats));
      }
    }

    if (entity?.wall?.positions) {
      const positions = entity.wall.positions.getValue(time) || [];
      if (positions.length) {
        const cartos = positions.map((p) => Cesium.Cartographic.fromCartesian(p));
        const lons = cartos.map((c) => c.longitude);
        const lats = cartos.map((c) => c.latitude);
        return new Cesium.Rectangle(Math.min(...lons), Math.min(...lats), Math.max(...lons), Math.max(...lats));
      }
    }

    return null;
  };

  const finalize = () => {
    const rect = rectangleFromTwoPixels(startPx, endPx);
    if (!rect) {
      cleanup();
      rejectPromise(new Error("Selection rectangle could not be computed (no surface pick)."));
      return;
    }

    const time = viewer.clock.currentTime;

    // Output array: [{ id, layerName, geojson }, ...]
    const out = [];

    for (const item of mapObject || []) {
      const ds = item?.dataSource;
      if (!ds) continue;

      const layerId = getLayerIdFromDataSource(ds);

      if (String(layerId) !== String(item.id)) continue;

      const entities = ds.entities?.values || [];
      const matched = [];

      for (const ent of entities) {
        const entRect = getEntityRectApprox(ent, time);
        if (!entRect) continue;
        if (rectIntersects(rect, entRect)) matched.push(ent);
      }

      if (!matched.length) continue;

      const features = [];
      for (const ent of matched) {
        const f = entityToGeoJsonFeature(ent, time);
        if (f) features.push(f);
      }

      if (!features.length) continue;

      out.push({
        id: String(layerId),
        layerName: getLayerNameFromDataSource(ds),
        geojson: {
          type: "FeatureCollection",
          features,
        },
      });
    }

    cleanup();
    resolvePromise(out);
  };

  // Active
  if (onActiveChange) onActiveChange(true);

  // --- Mouse events ---
  handler.setInputAction((evt) => {
    drawing = true;

    try {
      cameraLocked = disableCameraControls(viewer, originalCameraControls) === true;
    } catch {
      cameraLocked = false;
    }

    startPx = new Cesium.Cartesian2(evt.position.x, evt.position.y);
    endPx = new Cesium.Cartesian2(evt.position.x, evt.position.y);
    setBox(startPx, endPx);
  }, Cesium.ScreenSpaceEventType.LEFT_DOWN);

  handler.setInputAction((evt) => {
    if (!drawing) return;
    endPx = new Cesium.Cartesian2(evt.endPosition.x, evt.endPosition.y);
    setBox(startPx, endPx);
  }, Cesium.ScreenSpaceEventType.MOUSE_MOVE);

  handler.setInputAction(() => {
    if (!drawing) return;
    drawing = false;
    finalize();
  }, Cesium.ScreenSpaceEventType.LEFT_UP);

  handler.setInputAction(() => cancel(), Cesium.ScreenSpaceEventType.RIGHT_CLICK);

  return { promise, cancel };
}
